import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const courses = [
  { label: 'BE / BTech', icon: '🎓', count: '2000+', color: 'from-blue-100 to-indigo-100', border: 'border-blue-200', text: 'text-blue-800' },
  { label: 'MTech / ME', icon: '🔬', count: '800+', color: 'from-purple-100 to-violet-100', border: 'border-purple-200', text: 'text-purple-800' },
  { label: 'BSc', icon: '📐', count: '600+', color: 'from-green-100 to-emerald-100', border: 'border-green-200', text: 'text-green-800' },
  { label: 'Diploma', icon: '📋', count: '400+', color: 'from-orange-100 to-amber-100', border: 'border-orange-200', text: 'text-orange-800' },
  { label: 'MCA', icon: '💻', count: '500+', color: 'from-cyan-100 to-sky-100', border: 'border-cyan-200', text: 'text-cyan-800' },
  { label: 'MBA', icon: '📊', count: '300+', color: 'from-rose-100 to-pink-100', border: 'border-rose-200', text: 'text-rose-800' },
  { label: 'PhD', icon: '🏆', count: '50+', color: 'from-yellow-100 to-amber-100', border: 'border-yellow-300', text: 'text-yellow-800' },
  { label: 'PG Courses', icon: '📚', count: '700+', color: 'from-slate-100 to-gray-100', border: 'border-slate-200', text: 'text-slate-700' },
];

const domains = [
  { name: 'Artificial Intelligence', gradient: 'from-amber-400/20 to-orange-400/20', border: 'border-amber-300/40', icon: '🤖' },
  { name: 'Machine Learning', gradient: 'from-purple-400/20 to-indigo-400/20', border: 'border-purple-300/40', icon: '🧠' },
  { name: 'Internet of Things', gradient: 'from-green-400/20 to-teal-400/20', border: 'border-green-300/40', icon: '📡' },
  { name: 'Cybersecurity', gradient: 'from-red-400/20 to-rose-400/20', border: 'border-red-300/40', icon: '🔐' },
  { name: 'Cloud Computing', gradient: 'from-sky-400/20 to-blue-400/20', border: 'border-sky-300/40', icon: '☁️' },
  { name: 'Blockchain', gradient: 'from-yellow-400/20 to-amber-400/20', border: 'border-yellow-300/40', icon: '⛓️' },
  { name: 'AR / VR', gradient: 'from-pink-400/20 to-fuchsia-400/20', border: 'border-pink-300/40', icon: '🥽' },
  { name: 'Robotics', gradient: 'from-gray-400/20 to-slate-400/20', border: 'border-gray-300/40', icon: '🦾' },
  { name: 'Data Science', gradient: 'from-cyan-400/20 to-teal-400/20', border: 'border-cyan-300/40', icon: '📈' },
  { name: 'Bioinformatics', gradient: 'from-lime-400/20 to-green-400/20', border: 'border-lime-300/40', icon: '🧬' },
  { name: 'Quantum Computing', gradient: 'from-violet-400/20 to-purple-400/20', border: 'border-violet-300/40', icon: '⚛️' },
  { name: 'Edge Computing', gradient: 'from-blue-400/20 to-indigo-400/20', border: 'border-blue-300/40', icon: '🖥️' },
];

export default function CoursesSection() {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.1 });

  return (
    <section id="courses" className="section-pad relative overflow-hidden" style={{ background: '#FAFAF5' }}>
      {/* Decorative top border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-royal-gold to-transparent opacity-50" />
      
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#0B1C4E 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Courses We Serve */}
        <div ref={ref} className="mb-24">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              className="inline-flex items-center gap-2 bg-white border border-royal-gold/30 text-royal-gold-light text-sm font-semibold px-4 py-2 rounded-full mb-6 shadow-sm"
              style={{ color: '#A07830' }}
            >
              <span className="w-2 h-2 rounded-full bg-royal-gold animate-pulse" />
              Programs We Serve
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="font-playfair text-4xl md:text-5xl font-bold text-royal-navy mb-4"
            >
              For Every{' '}
              <span className="text-gradient-gold">Academic Level</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.2 }}
              className="font-inter text-slate-royal/70 text-lg max-w-xl mx-auto"
            >
              Tailored academic project support from diploma to doctoral level
            </motion.p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {courses.map((course, i) => (
              <motion.div
                key={course.label}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={inView ? { opacity: 1, scale: 1 } : {}}
                transition={{ delay: i * 0.08, type: 'spring', stiffness: 200 }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="bg-white rounded-2xl p-6 text-center cursor-pointer group shadow-card border border-parchment-dark hover:border-royal-gold hover:shadow-gold transition-all duration-300"
                id={`course-${course.label.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${course.color} border ${course.border} mb-4 text-3xl group-hover:scale-110 transition-transform duration-300`}>
                  {course.icon}
                </div>
                <p className={`font-playfair font-bold text-royal-navy text-base mb-1`}>{course.label}</p>
                <p className="font-inter text-xs text-royal-gold font-semibold">{course.count} Projects</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Technology Domains */}
        <div id="domains">
          <div className="text-center mb-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              className="inline-flex items-center gap-2 bg-white border border-royal-gold/30 text-royal-gold-light text-sm font-semibold px-4 py-2 rounded-full mb-6 shadow-sm"
              style={{ color: '#A07830' }}
            >
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
              </svg>
              Technology Domains
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.1 }}
              className="font-playfair text-4xl md:text-5xl font-bold text-royal-navy mb-4"
            >
              Expertise Across{' '}
              <span className="text-gradient-gold">All Domains</span>
            </motion.h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {domains.map((domain, i) => (
              <motion.div
                key={domain.name}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.07 }}
                whileHover={{ scale: 1.04, y: -4 }}
                className={`bg-white border border-parchment-dark shadow-sm hover:shadow-gold hover:border-royal-gold rounded-2xl p-4 flex items-center gap-3 group cursor-pointer transition-all duration-300`}
              >
                <span className={`text-3xl bg-gradient-to-br ${domain.gradient} p-2 rounded-xl group-hover:scale-110 transition-transform duration-300`}>{domain.icon}</span>
                <span className="font-inter font-semibold text-royal-navy text-sm group-hover:text-royal-gold transition-colors">{domain.name}</span>
              </motion.div>
            ))}
          </div>

          {/* CTA to domains page */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.8 }}
            className="text-center mt-10"
          >
            <a
              href="/domains"
              className="inline-flex items-center gap-2 font-inter font-bold px-8 py-4 rounded-full text-royal-navy text-base transition-all hover:scale-105 hover:shadow-gold-lg"
              style={{ background: 'linear-gradient(135deg, #C9A84C, #F0D080)', boxShadow: '0 4px 20px rgba(201,168,76,0.4)' }}
            >
              Explore All Domains in Detail
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </a>
            <p className="font-inter text-slate-royal/60 text-xs mt-4">Deliverables, technologies & sample projects for every domain</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
